import { MousePointerClick, Server, FileText, Gauge } from 'lucide-react';

type DetectionMethod = 'honeypot' | 'datacenter' | 'header' | 'velocity';

interface DetectionMethodBadgeProps {
  method: DetectionMethod;
  details?: string;
  count?: number;
}

const methodConfig: Record<DetectionMethod, { label: string; icon: typeof Server; color: string; description: string }> = {
  honeypot: {
    label: 'Honeypot',
    icon: MousePointerClick,
    color: 'red',
    description: 'Accessed trap endpoint',
  },
  datacenter: {
    label: 'Datacenter',
    icon: Server,
    color: 'blue',
    description: 'Cloud/hosting infrastructure',
  },
  header: {
    label: 'Header Anomaly',
    icon: FileText,
    color: 'yellow',
    description: 'Suspicious HTTP headers',
  },
  velocity: {
    label: 'High Velocity',
    icon: Gauge,
    color: 'orange',
    description: 'Rapid request rate',
  },
};

/**
 * Detection Method Badge
 * 
 * Displays a specific SGIS detection method with icon and tooltip
 */
export function DetectionMethodBadge({ method, details, count }: DetectionMethodBadgeProps) {
  const config = methodConfig[method];
  const Icon = config.icon;

  const colorClasses = {
    red: 'bg-red-500/10 text-red-400 border-red-500/30',
    blue: 'bg-blue-500/10 text-blue-400 border-blue-500/30',
    yellow: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30',
    orange: 'bg-orange-500/10 text-orange-400 border-orange-500/30',
  };

  const tooltipText = details || config.description;
  const displayText = count !== undefined ? `${config.label} (${count})` : config.label;

  return (
    <div 
      className={`inline-flex items-center gap-2 px-2.5 py-1 rounded border text-xs font-medium ${colorClasses[config.color]}`}
      title={tooltipText}
    >
      <Icon className="w-3.5 h-3.5" />
      <span>{displayText}</span>
    </div>
  );
}

/**
 * Detection Methods Group
 * 
 * Displays all active detection methods for a threat
 */
interface DetectionMethodsGroupProps {
  honeypot?: boolean;
  datacenter?: boolean;
  headerAnomalies?: string[];
  requestsPerMinute?: number;
}

export function DetectionMethodsGroup({ 
  honeypot, 
  datacenter, 
  headerAnomalies, 
  requestsPerMinute 
}: DetectionMethodsGroupProps) {
  const hasAnyDetection = honeypot || datacenter || (headerAnomalies && headerAnomalies.length > 0) || (requestsPerMinute && requestsPerMinute > 30);

  if (!hasAnyDetection) {
    return (
      <div className="text-sm text-gray-500 italic">
        No special detection methods triggered
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      {honeypot && (
        <DetectionMethodBadge 
          method="honeypot" 
          details="Accessed honeypot trap path" 
        />
      )}
      {datacenter && (
        <DetectionMethodBadge 
          method="datacenter" 
          details="Traffic from cloud/datacenter ASN" 
        />
      )}
      {headerAnomalies && headerAnomalies.length > 0 && (
        <DetectionMethodBadge 
          method="header" 
          count={headerAnomalies.length}
          details={`Detected: ${headerAnomalies.join(', ')}`}
        />
      )}
      {requestsPerMinute && requestsPerMinute > 30 && (
        <DetectionMethodBadge 
          method="velocity" 
          details={`${requestsPerMinute} requests/min`}
        />
      )}
    </div>
  );
}
